import { useState, useEffect } from 'react';
import { PhotoIcon, Square2StackIcon } from '@heroicons/react/20/solid';
import { Switch, Tab } from '@headlessui/react';
import { toast } from 'react-toastify';

import WaitingScreenService from '../../services/waitingscreen.service';
import RichTextEditor from '../../components/admin/richtexteditor.component';
import WaitingComponent from '../../components/waiting.component';
import ScaledPreview from '../../components/ui/scaledpreview.component';

const LANGS = [
  { key: 'nl', label: 'NL', placeholders: { pretitle: 'Ons nieuwste investeringsproject', title: 'Flow Upkot', button: 'Ontdek Flow Upkot' } },
  { key: 'fr', label: 'FR', placeholders: { pretitle: "Notre dernier projet d'investissement", title: 'Flow Upkot', button: 'Découvrir Flow Upkot' } },
  { key: 'en', label: 'EN', placeholders: { pretitle: 'Our latest investment project', title: 'Flow Upkot', button: 'Discover Flow Upkot' } },
];

// Fields every card has; the second card stores them with a "card2_" prefix.
const CARD_FIELDS = ['image', 'button_url', ...['pretitle', 'title', 'subtitle', 'button_text'].flatMap(
  (field) => LANGS.map((lang) => `${field}_${lang.key}`)
)];

const CARDS = [
  { prefix: '', activeKey: 'active', title: 'Kaart 1', description: 'Staat bovenaan.' },
  { prefix: 'card2_', activeKey: 'card2_active', title: 'Kaart 2', description: 'Staat eronder, iets naar rechts.' },
];

const PREVIEW_FALLBACK = {
  title: { nl: 'Titel', fr: 'Titre', en: 'Title' },
  subtitle: { nl: 'Subtitel...', fr: 'Sous-titre...', en: 'Subtitle...' },
  button_text: { nl: 'Button tekst', fr: 'Texte du bouton', en: 'Button text' },
};

const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';

function emptyCard(active) {
  const card = { file: null, active };
  CARD_FIELDS.forEach((field) => { card[field] = ''; });
  return card;
}

function cardFromApi(data, { prefix, activeKey }) {
  const card = emptyCard(false);
  CARD_FIELDS.forEach((field) => { card[field] = data[prefix + field] || ''; });
  card.active = activeKey === 'active'
    ? (data.active !== undefined ? data.active : true)
    : Boolean(data[activeKey]);
  return card;
}

function cardToApi(card, { prefix, activeKey }) {
  const out = { [activeKey]: card.active };
  CARD_FIELDS.forEach((field) => { out[prefix + field] = card[field]; });
  return out;
}

function filled(value) {
  return (value || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';
}

function validateCard(card, label) {
  if (!card.active) return [];
  const missing = [];
  if (!card.image && !card.file) missing.push('een afbeelding');
  [['title', 'de titel'], ['subtitle', 'de subtitel'], ['button_text', 'de knoptekst']].forEach(([field, name]) => {
    if (!LANGS.every((lang) => filled(card[`${field}_${lang.key}`]))) missing.push(`${name} (NL, FR en EN)`);
  });
  if (!filled(card.button_url)) missing.push('de link');
  return missing.length ? [`${label}: vul ${missing.join(', ')} in.`] : [];
}

function useObjectUrl(file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url;
}

function imageUrl(card, localUrl) {
  if (localUrl) return localUrl;
  if (card.image) return process.env.REACT_APP_API_URI + 'file/download/' + card.image;
  return null;
}

function CardEditor({ index, meta, card, localUrl, onChange }) {
  const shownImage = imageUrl(card, localUrl);
  const fileInputId = `waiting-card-image-${index}`;

  return (
    <section className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold leading-6 text-gray-900">{meta.title}</h3>
          <div className="mt-1 text-sm text-gray-500">
            {meta.description} {card.active ? 'Wordt getoond aan deelnemers.' : 'Wordt niet getoond.'}
          </div>
        </div>
        <Switch.Group as="div" className="flex flex-shrink-0 items-center gap-3">
          <Switch.Label className="text-sm font-medium text-gray-900">Tonen</Switch.Label>
          <Switch
            checked={card.active}
            onChange={(active) => onChange({ active })}
            className={`${card.active ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
          >
            <span className={`${card.active ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
          </Switch>
        </Switch.Group>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div>
          <div className="mb-1 text-sm font-medium text-gray-900">Afbeelding</div>
          {shownImage ? (
            <div>
              <img src={shownImage} alt="" className="mb-3 h-64 w-full rounded-lg object-cover" />
              <button
                type="button"
                onClick={() => onChange({ image: '', file: null })}
                className="w-full rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Afbeelding verwijderen
              </button>
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 hover:border-gray-400">
              <div className="text-center">
                <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" aria-hidden="true" />
                <div className="mt-4 text-sm leading-6 text-gray-600">
                  <label
                    htmlFor={fileInputId}
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                  >
                    <span>Upload een bestand</span>
                    <input
                      id={fileInputId}
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={(e) => onChange({ file: e.target.files[0] || null })}
                    />
                  </label>
                  <div className="mt-1 text-xs text-gray-500">PNG, JPG tot 10MB</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <Tab.Group>
            <Tab.List className="mb-4 flex space-x-1">
              {LANGS.map((lang) => (
                <Tab
                  key={lang.key}
                  className={({ selected }) => `w-full rounded-md py-2 text-sm font-medium transition-all ${selected ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {lang.label}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              {LANGS.map((lang) => (
                <Tab.Panel key={lang.key} className="space-y-3">
                  <div>
                    <label htmlFor={`card${index}-pretitle-${lang.key}`} className="mb-1 block text-sm font-medium text-gray-900">Pretitel</label>
                    <input
                      id={`card${index}-pretitle-${lang.key}`}
                      type="text"
                      value={card[`pretitle_${lang.key}`]}
                      onChange={(e) => onChange({ [`pretitle_${lang.key}`]: e.target.value })}
                      placeholder={lang.placeholders.pretitle}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor={`card${index}-title-${lang.key}`} className="mb-1 block text-sm font-medium text-gray-900">Titel</label>
                    <input
                      id={`card${index}-title-${lang.key}`}
                      type="text"
                      value={card[`title_${lang.key}`]}
                      onChange={(e) => onChange({ [`title_${lang.key}`]: e.target.value })}
                      placeholder={lang.placeholders.title}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <div className="mb-1 block text-sm font-medium text-gray-900">Subtitel</div>
                    <RichTextEditor
                      value={card[`subtitle_${lang.key}`]}
                      onChange={(value) => onChange({ [`subtitle_${lang.key}`]: value })}
                      placeholder="Beschrijving..."
                    />
                  </div>
                  <div>
                    <label htmlFor={`card${index}-button-${lang.key}`} className="mb-1 block text-sm font-medium text-gray-900">Button tekst</label>
                    <input
                      id={`card${index}-button-${lang.key}`}
                      type="text"
                      value={card[`button_text_${lang.key}`]}
                      onChange={(e) => onChange({ [`button_text_${lang.key}`]: e.target.value })}
                      placeholder={lang.placeholders.button}
                      className={inputClass}
                    />
                  </div>
                </Tab.Panel>
              ))}
            </Tab.Panels>
          </Tab.Group>

          <div className="mt-4">
            <label htmlFor={`card${index}-url`} className="mb-1 block text-sm font-medium text-gray-900">Link van de knop</label>
            <input
              id={`card${index}-url`}
              type="text"
              value={card.button_url}
              onChange={(e) => onChange({ button_url: e.target.value })}
              placeholder="https://upgrade-estate.be/nl/investeren/..."
              className={inputClass}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AdminWachtschermView() {
  const [cards, setCards] = useState([emptyCard(true), emptyCard(false)]);
  const [loading, setLoading] = useState(false);
  const [previewLang, setPreviewLang] = useState('nl');

  // Fixed target for the preview countdown: tomorrow at 19:00
  const [previewDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(19, 0, 0, 0);
    return d;
  });

  // Object URLs for newly selected (not yet uploaded) images
  const localUrls = [useObjectUrl(cards[0].file), useObjectUrl(cards[1].file)];

  useEffect(() => {
    WaitingScreenService.getAdmin()
      .then((response) => {
        if (response.data) {
          setCards(CARDS.map((meta) => cardFromApi(response.data, meta)));
        }
      })
      .catch(() => {
        console.log('No waiting screen configured yet');
      });
  }, []);

  function updateCard(index, patch) {
    setCards((prev) => prev.map((card, i) => (i === index ? { ...card, ...patch } : card)));
  }

  async function uploadIfNeeded(card) {
    if (!card.file) return card;
    const formData = new FormData();
    formData.append('file', card.file);
    const response = await WaitingScreenService.uploadFile(formData);
    return { ...card, image: response.data, file: null };
  }

  async function saveWaitingScreen() {
    const errors = CARDS.flatMap((meta, i) => validateCard(cards[i], meta.title));
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    setLoading(true);
    try {
      const uploaded = await Promise.all(cards.map(uploadIfNeeded));
      const payload = Object.assign({}, ...CARDS.map((meta, i) => cardToApi(uploaded[i], meta)));
      const response = await WaitingScreenService.update(payload);
      setCards(CARDS.map((meta) => cardFromApi(response.data, meta)));
      toast.success('Wachtscherm succesvol aangepast.');
    } catch (error) {
      console.log(error);
      toast.error(typeof error?.response?.data === 'string'
        ? error.response.data
        : 'Er is iets fout gegaan bij het aanpassen van het wachtscherm.');
    } finally {
      setLoading(false);
    }
  }

  // What the preview shows: the current (unsaved) state, with placeholders for empty texts
  const previewData = Object.assign({}, ...CARDS.map((meta, i) => {
    const card = cards[i];
    const out = {
      [meta.activeKey]: card.active,
      [`${meta.prefix}image`]: imageUrl(card, localUrls[i]),
      [`${meta.prefix}button_url`]: card.button_url,
    };
    LANGS.forEach(({ key }) => {
      out[`${meta.prefix}pretitle_${key}`] = card[`pretitle_${key}`];
      ['title', 'subtitle', 'button_text'].forEach((field) => {
        out[`${meta.prefix}${field}_${key}`] = card[`${field}_${key}`] || PREVIEW_FALLBACK[field][key];
      });
    });
    return out;
  }));

  const shownCount = cards.filter((card) => card.active).length;

  return (
    <main className="flex-1">
      <div className="py-6">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 sm:flex sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Wachtscherm</h1>
            <div className="mt-1 text-sm text-gray-500">
              De pagina die deelnemers zien zolang de meeting niet gestart is, met tot twee projectkaarten.
            </div>
          </div>
          <div className="mt-3 sm:mt-0 sm:ml-4">
            <button
              type="button"
              onClick={saveWaitingScreen}
              disabled={loading}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <Square2StackIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              {loading ? 'Bezig...' : 'Opslaan'}
            </button>
          </div>
        </div>

        <div className="mx-auto px-4 sm:px-6 md:px-8 mt-8 space-y-6">
          {CARDS.map((meta, i) => (
            <CardEditor
              key={meta.activeKey}
              index={i}
              meta={meta}
              card={cards[i]}
              localUrl={localUrls[i]}
              onChange={(patch) => updateCard(i, patch)}
            />
          ))}

          {/* Full-page preview: the real waiting screen, scaled to fit */}
          <section className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900">Voorbeeld</h3>
                <div className="mt-1 text-sm text-gray-500">
                  {shownCount === 0
                    ? 'Er staat geen kaart aan. Deelnemers zien enkel de aftelling.'
                    : 'De wachtpagina zoals deelnemers ze te zien krijgen. Kaarten die uit staan, worden niet getoond.'}
                </div>
              </div>
              <div className="flex flex-shrink-0 space-x-2">
                {LANGS.map((lang) => (
                  <button
                    key={lang.key}
                    type="button"
                    onClick={() => setPreviewLang(lang.key)}
                    className={`px-3 py-1 rounded text-sm font-medium ${previewLang === lang.key ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            <ScaledPreview
              width={1920}
              height={1080}
              className="rounded-lg pointer-events-none select-none"
            >
              <WaitingComponent
                preview
                date={previewDate}
                lang={previewLang}
                previewData={previewData}
              />
            </ScaledPreview>
          </section>
        </div>
      </div>
    </main>
  );
}
